import { useMutation, useApolloClient } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { useMemo, useCallback } from 'react';

import { type DeleteAuditDecisionResponse, type DeleteAuditDecisionVariables } from '../domain/graphql-types';
import { DELETE_AUDIT_DECISION } from '../graphql/mutations/deleteAuditDecision';
import { updateAuditItemAndStats, createOptimisticDeleteResponse, removeDecisionFromCache } from '../utils/auditCacheUpdates';

/**
 * Hook for deleting audit decisions with optimistic updates
 * Enhanced with adapter pattern for better error handling and separation of concerns
 * Includes strategic business event tracking for observability compliance
 */
export function useDeleteAuditDecision() {
  const client = useApolloClient();
  const [deleteAuditDecisionMutation, { data, loading, error }] = useMutation<DeleteAuditDecisionResponse, DeleteAuditDecisionVariables>(
    DELETE_AUDIT_DECISION,
    {
      errorPolicy: 'all',
      update: (cache, { data: mutationData }) => {
        // Only update cache if the mutation was successful
        if (mutationData?.deleteAuditDecision) {
          const { auditItem } = mutationData.deleteAuditDecision;

          // Update audit item and invalidate stats for reactive progress updates
          updateAuditItemAndStats(cache, auditItem);
        }
      },
    }
  );

  // Use GraphQL data directly
  const updatedAuditItem = useMemo(() => {
    if (!data?.deleteAuditDecision) {
      return undefined;
    }

    return data.deleteAuditDecision.auditItem;
  }, [data]);

  // Transform errors using simplified adapter pattern - return array for consistency
  const domainErrors = useMemo(() => {
    if (!error) return [];

    // Map GraphQL errors to user-friendly format
    const mappedError = transformError(error);

    // Return error array for UI handling
    return [mappedError];
  }, [error]);

  const deleteAuditDecision = useCallback(
    // eslint-disable-next-line complexity
    async (variables: DeleteAuditDecisionVariables) => {
      const { input } = variables;

      // Track audit decision deletion initiation
      track('DeleteAuditDecisionInitiated', {
        decisionId: input.decisionId,
        auditItemId: input.auditItemId,
        auditSessionId: input.auditSessionId,
      });

      try {
        // Create optimistic response using centralized utility
        const optimisticResponse = createOptimisticDeleteResponse(client.cache, input.auditItemId, input.decisionId);

        // Optimistically remove the decision from the decisions list immediately
        removeDecisionFromCache(client.cache, input.decisionId);

        const result = await deleteAuditDecisionMutation({
          variables,
          ...(optimisticResponse && { optimisticResponse }),
          update: (cache, { data: mutationData }) => {
            // Only run this update for successful mutations (not optimistic)
            if (mutationData?.deleteAuditDecision) {
              const { auditItem } = mutationData.deleteAuditDecision;

              // Update audit item and invalidate stats for reactive progress updates
              updateAuditItemAndStats(cache, auditItem);
            }
          },
        });

        // Check for success: data exists and no errors
        if (result.data?.deleteAuditDecision && !result.errors) {
          const auditItemData = result.data.deleteAuditDecision.auditItem;

          // Track successful audit decision deletion
          track('DeleteAuditDecisionSucceeded', {
            decisionId: input.decisionId,
            auditItemId: input.auditItemId,
            auditSessionId: input.auditSessionId,
            remainingDecisionsCount: auditItemData.decisionsCount.toString(),
          });

          return {
            success: true,
            data: auditItemData,
          };
        }

        // Handle errors - normalize to always use errors array
        const errors = result.errors || [];
        const mappedErrors = errors.map(transformError);
        const firstError = mappedErrors[0];

        // Track audit decision deletion failure
        track('DeleteAuditDecisionFailed', {
          decisionId: input.decisionId,
          auditItemId: input.auditItemId,
          auditSessionId: input.auditSessionId,
          errorType: 'api_error',
          errorMessage: firstError?.message || 'Unknown error',
        });

        return {
          success: false,
          errors: mappedErrors,
        };
      } catch (error) {
        // Track unexpected audit decision deletion failure
        track('DeleteAuditDecisionFailed', {
          decisionId: input.decisionId,
          auditItemId: input.auditItemId,
          auditSessionId: input.auditSessionId,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [deleteAuditDecisionMutation, client.cache]
  );

  return {
    deleteAuditDecision,
    updatedAuditItem,
    loading,
    errors: domainErrors,
  };
}
